import type { FieldPath, FieldValues } from 'react-hook-form';
import type {
  AppFormFieldButtonGroupProps,
  AppFormFieldCheckboxProps,
  AppFormFieldProps,
  AppFormFieldRadioCardProps,
  AppFormFieldSelectProps,
  AppFormFieldTabsProps,
  AppFormFieldTextareaProps,
} from './types';

export const getIn = (obj: unknown, path: string): unknown =>
  path.split('.').reduce((current: unknown, key) => {
    if (current == null || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[key];
  }, obj);

export const getErrorMessage = (error: unknown): string | undefined =>
  typeof error === 'string' ? error : (error as { message?: string })?.message;

export const omitKeys = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const set = new Set(keys);
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !set.has(k as K))
  ) as Omit<T, K>;
};

export const isCheckboxProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldCheckboxProps<TFieldValues, TName> =>
  props.fieldType === 'checkbox';

export const isTabsProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldTabsProps<TFieldValues, TName> =>
  props.fieldType === 'tabs';

export const isSelectProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldSelectProps<TFieldValues, TName> =>
  props.fieldType === 'select';

export const isTextareaProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldTextareaProps<TFieldValues, TName> =>
  props.fieldType === 'textarea';

export const isRadioCardProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldRadioCardProps<TFieldValues, TName> =>
  props.fieldType === 'radio-card';

export const isButtonGroupProps = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: AppFormFieldProps<TFieldValues, TName>
): props is AppFormFieldButtonGroupProps<TFieldValues, TName> =>
  props.fieldType === 'button-group';
